import React from "react";
import cx from "classnames";
import includes from "lodash/includes";
import isEqual from "lodash/isEqual";
import uniqWith from "lodash/uniqWith";
import PropTypes from "prop-types";

import DropdownInput from "components/Dropdown/DropdownInput";
import DropdownItem from "components/Dropdown/DropdownItem";
import SelectAll from "components/SelectAll";

import styles from "./Dropdown.css";

class Dropdown extends React.Component {
  constructor(props) {
    super(props);
    this.state = { isOpen: props.isOpen || false };
    this.getSelectedValues = this.getSelectedValues.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleOpen = this.handleOpen.bind(this);
    this.handleSelectAll = this.handleSelectAll.bind(this);
    this.isEqual = this.isEqual.bind(this);
    this.isSelected = this.isSelected.bind(this);
    this.onToggle = this.onToggle.bind(this);
    this.selectValue = this.selectValue.bind(this);
    this.dropdown = React.createRef();
  }

  componentWillUnmount() {
    this.handleClose();
  }

  handleOpen() {
    this.setState({ isOpen: true });
    document.addEventListener("click", this.handleClose, false);
    document.addEventListener("touchend", this.handleClose, false);
  }

  handleClear() {
    const { isMultiple, onChange } = this.props;
    this.handleClose();
    onChange(isMultiple ? [] : undefined);
  }

  handleClose(evt = null) {
    if (!this.dropdown.current || (evt && this.dropdown.current.contains(evt.target))) {
      return;
    }
    this.setState({ isOpen: false });
    if (this.props.onClose) this.props.onClose();
    document.removeEventListener("click", this.handleClose, false);
    document.removeEventListener("touchend", this.handleClose, false);
  }

  handleSelectAll() {
    const { onChange, values } = this.props;
    onChange(values);
    this.handleClose();
  }

  onToggle() {
    const { isOpen } = this.state;
    if (!isOpen) return this.handleOpen();

    return this.handleClose();
  }

  getSelectedValues() {
    const { keyExtractor, selectedKeys, selectedValues, values } = this.props;

    return selectedValues || values.filter((v) => includes(selectedKeys, keyExtractor(v)));
  }

  isEqual(value1, value2) {
    const { keyExtractor } = this.props;
    if (!keyExtractor) return isEqual(value1, value2);

    return isEqual(keyExtractor(value1), keyExtractor(value2));
  }

  isSelected(values, value) {
    return !!values.find((v) => this.isEqual(value, v));
  }

  selectValue(evt, value) {
    const { isMultiple, isUnselectable, onChange } = this.props;
    const selectedValues = this.getSelectedValues();
    const isValueSelected = this.isSelected(selectedValues, value);

    if (evt) evt.stopPropagation();
    if (!isMultiple && !isUnselectable && isValueSelected) return this.handleClose();

    let returnValue = value;

    if (isMultiple) {
      if (isValueSelected) returnValue = selectedValues.filter((v) => !this.isEqual(v, value));
      if (!isValueSelected) returnValue = uniqWith([...selectedValues, value], this.isEqual);
    }

    if (!isMultiple && isValueSelected && isUnselectable) returnValue = null;

    if (!isMultiple) this.handleClose();

    return onChange(returnValue);
  }

  render() {
    const {
      align,
      className,
      component,
      componentProps,
      header,
      inputComponent,
      inputComponentProps,
      inputValueExtractor,
      isFullWidth,
      isMultiple,
      placeholder,
      selectedKeys,
      selectedValues,
      sortBy,
      valueExtractor,
      values,
    } = this.props;

    const { isOpen } = this.state;

    const InputComponent = inputComponent;
    const DropdownComponent = component;

    const selected = this.getSelectedValues();
    const sortedValues = sortBy(values);

    return (
      <div
        className={cx(styles.root, className, {
          [styles.fullWidth]: isFullWidth,
        })}
        ref={this.dropdown}
      >
        <div
          className={cx(styles.input, { [styles.disabled]: !values.length })}
          onClick={this.onToggle}
        >
          <InputComponent
            isOpen={isOpen}
            selectedValues={selected}
            placeholder={placeholder}
            value={
              !isMultiple &&
              !!selected.length &&
              (inputValueExtractor || valueExtractor)(selected[0])
            }
            {...inputComponentProps}
          />
        </div>
        {isOpen && (
          <div className={cx(styles.dropdown, styles[`${align}Align`])}>
            <div>
              {header && <div className={styles.header}>{header}</div>}
              {isMultiple && (
                <SelectAll
                  allSelected={(selectedKeys || selectedValues).length === values.length}
                  isSelected={(selectedKeys || selectedValues).length > 0}
                  onClear={this.handleClear}
                  onSelectAll={this.handleSelectAll}
                />
              )}
              {sortedValues.map((value, idx) => (
                <div key={idx} onClick={(evt) => this.selectValue(evt, value)}>
                  <DropdownComponent
                    isMultiple={isMultiple}
                    isSelected={this.isSelected(selected, value)}
                    value={valueExtractor(value)}
                    {...componentProps}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
}

Dropdown.propTypes = {
  align: PropTypes.oneOf(["left", "right"]),
  className: PropTypes.string,
  component: PropTypes.func,
  componentProps: PropTypes.object,
  header: PropTypes.string,
  inputComponent: PropTypes.func,
  inputComponentProps: PropTypes.object,
  inputValueExtractor: PropTypes.func,
  isMultiple: PropTypes.bool,
  isFullWidth: PropTypes.bool,
  isOpen: PropTypes.bool,
  isUnselectable: PropTypes.bool,
  keyExtractor: PropTypes.func,
  onChange: PropTypes.func,
  onClose: PropTypes.func,
  selectedKeys: PropTypes.array,
  selectedValues: PropTypes.array,
  sortBy: PropTypes.func,
  valueExtractor: PropTypes.func,
  values: PropTypes.array,
};

Dropdown.defaultProps = {
  align: "right",
  component: DropdownItem,
  inputComponent: DropdownInput,
  keyExtractor: (value) => value,
  sortBy: (value) => value,
  valueExtractor: (value) => value,
  values: [],
};

export default Dropdown;
